import { BaseError } from 'make-error-cause';
import { ResultAsync, err, ok, okAsync } from 'neverthrow';

const solrClientErrorSymbol = Symbol("SolrClientError");
const solrFetchErrorSymbol = Symbol("SolrFetchError");
const solrHttpErrorSymbol = Symbol("SolrHttpError");
const solrBadDataErrorSymbol = Symbol("SolrBadDataError");
/**
 * The root of the custom error hierarchy for vv-solr-client.
 *
 * Only use this for operational errors. For the distinction between operation errors and programmer errors, read:
 * https://www.joyent.com/node-js/production/design/errors
 *
 * For 'programmer errors', just use the normal Error class, and let the program crash.
 */
class SolrClientError extends BaseError {
    constructor(message, cause) {
        super(message, cause);
        // See https://github.com/supermacro/neverthrow/issues/451
        this.solrClientError = solrClientErrorSymbol;
    }
}
/**
 * The fetch method failed.
 */
class SolrFetchError extends SolrClientError {
    constructor() {
        super(...arguments);
        // See https://github.com/supermacro/neverthrow/issues/451
        this.solrFetchError = solrFetchErrorSymbol;
    }
}
/**
 * We got an unexpected HTTP status code.
 */
class SolrHttpError extends SolrClientError {
    constructor(message, cause, response, responseBody) {
        super(message, cause);
        // See https://github.com/supermacro/neverthrow/issues/451
        this.solrHttpError = solrHttpErrorSymbol;
        this.response = response;
        this.responseBody = responseBody;
    }
}
/**
 * We got bad data from the server.
 */
class SolrBadDataError extends SolrClientError {
    constructor() {
        super(...arguments);
        // See https://github.com/supermacro/neverthrow/issues/451
        this.solrBadDataError = solrBadDataErrorSymbol;
    }
}

function base64Encode(data) {
    if (typeof window === "undefined") {
        // Assume Node JS.
        return Buffer.from(data).toString("base64");
    }
    // Assume browser.
    return window.btoa(data);
}

function expectJsonResponseCached(cache, cacheKey, f, url, init = {}) {
    // Try the cache first.
    const cacheValue = cache.get(cacheKey);
    if (cacheValue) {
        // Cache hit.
        return cacheValue;
    }
    // Cache missed. Make a new request.
    const result = expectJsonResponse(f, url, init);
    // Cache the result for later.
    cache.set(cacheKey, result);
    return result;
}
function expectJsonResponse(f, url, init = {}) {
    return ResultAsync.fromPromise(f(url, init), (e) => {
        // This could be a network issue, etc. Remember that fetch does not throw for >=400 status codes.
        // This is an operational error, not a programmer error, so do not throw.
        return new SolrFetchError((e === null || e === void 0 ? void 0 : e.message) || `Unknown fetch error for ${url}.`, e);
    }).andThen((response) => {
        if (!response.ok) {
            // Response is not in the 2XX range.
            return getHttpErrorResult(response);
        }
        return getJsonResponseResult(response);
    });
}
/**
 * Given a Response object with a response code outside the 2XX range, get a failing result object containing the error.
 */
function getHttpErrorResult(response) {
    // Sometimes the response body contains more useful info about the error.
    return ResultAsync.fromPromise(response.text(), (e) => e)
        .orElse((e) => err(new SolrHttpError(response.statusText, e, response, "Failed to parse response body.")))
        .andThen((errorMessage) => err(new SolrHttpError(response.statusText, undefined, response, errorMessage)));
}
/**
 * Given a Response object with a response code in the 2XX range, get a result object representing the JSON data.
 */
function getJsonResponseResult(response) {
    return ResultAsync.fromPromise(response.json(), (e) => {
        // This could be a network issue, or the back end return invalid JSON.
        // This is an operational error, not a programmer error, so do not throw.
        return new SolrBadDataError((e === null || e === void 0 ? void 0 : e.message) || `Failed to get response body for ${response.url}.`, e);
    });
}

/**
 * An implementation of CacheStorage that does absolutely nothing.
 */
const noOpCache = {
    has: () => false,
    get: () => undefined,
    set: () => { },
    delete: () => { },
    clear: () => { },
};

/**
 * Assign a boost value to each field in an array, where the first value has the highest boost and the last value has
 * the lowest boost. Everything in between is evenly distributed over the range of numbers.
 */
function assignBoostInRange(min, max, fields) {
    const range = max - min;
    const step = 1 / (fields.length - 1);
    return fields.map((field, i) => {
        return {
            field,
            boost: roundTwoDecimals(min + range * (1 - i * step)),
        };
    });
}
function roundTwoDecimals(x) {
    return Math.round(x * 100) / 100;
}
class SolrClient {
    /**
     * Create a SolrClient object.
     *
     * @param baseUrl
     *   The base URL of the Solr server.
     * @param f
     *   The `fetch` function.
     * @param auth
     *   The username and password for accessing the solr server.
     * @param core
     *   The name of the solr core on which to run all queries.
     * @param cache
     *   An optional cache for preventing duplicate searches.
     *   For unlimited cache without expiry, a `Map` should work.
     *   Or you could try something like https://github.com/SamVerschueren/expiry-map .
     */
    constructor(baseUrl, f, auth, core, cache = noOpCache) {
        this.baseUrl = baseUrl;
        this.f = f;
        this.auth = base64Encode(`${auth.username}:${auth.password}`);
        this.core = core;
        this.cache = cache;
    }
    sign(init) {
        return Object.assign(Object.assign({}, init), { headers: Object.assign(Object.assign({}, (init.headers || {})), { Authorization: `Basic ${this.auth}` }) });
    }
    /**
     * Helper for POST requests that send JSON data.
     *
     * @param path
     *   Path, relative to base URL.
     * @param data
     *   JSON-serializable data to send.
     *
     * @returns
     *   A result object containing decoded JSON data or an error.
     */
    postJson(path, data) {
        const method = "POST";
        const href = new URL(path, this.baseUrl).href;
        const body = JSON.stringify(data);
        return expectJsonResponseCached(this.cache, `${method}:${href}:${body}`, this.f, href, this.sign({
            method,
            headers: {
                "Content-Type": "application/json",
            },
            body,
        }));
    }
    /** @inheritDoc */
    select(data) {
        return this.postJson(`solr/${this.core}/select`, data);
    }
    /** @inheritDoc */
    countInRanges(vidRanges, languages) {
        const filter = [
            // Only count published comments
            "bs_status:true",
            "index_id:commentary_comments",
        ];
        if (languages.length) {
            filter.push(`ss_search_api_language:(${languages.join(" OR ")})`);
        }
        const result = this.select({
            query: "*:*",
            limit: 0,
            filter,
            facet: {
                vidRanges: {
                    type: "range",
                    field: "itm_scripture_vids",
                    ranges: vidRanges.map((r) => ({
                        from: r.fromVid,
                        to: r.toVid,
                        inclusive_from: true,
                        inclusive_to: true,
                    })),
                    facet: {
                        languages: {
                            type: "terms",
                            field: "ss_search_api_language",
                            facet: {
                                commentaryType: {
                                    type: "terms",
                                    field: "sm_type_short_name",
                                },
                            },
                        },
                    },
                },
            },
        });
        return result.andThen((data) => {
            const facets = data.facets;
            if (!facets) {
                return err(new SolrBadDataError("Did not find 'facets' in response data."));
            }
            // Assume the data from the server is correct.
            return ok(facets);
        });
    }
    /** @inheritDoc */
    queryCommentary(vidRange, commentaryTypeShortName, detailLevelWeight, published, languages, offset, limit, groupOffset, groupLimit, fields) {
        const filter = [
            `{!tag=COMMENTARY_TYPE}sm_type_short_name:${commentaryTypeShortName}`,
            "index_id:(commentary_comments OR commentary_images)",
            `{!tag=LANGUAGE}ss_search_api_language:(${languages.join(" OR ")})`,
            `itm_scripture_vids:[${vidRange.fromVid} TO ${vidRange.toVid}]`,
        ];
        if (detailLevelWeight !== null) {
            // Show all detail levels up to the given level.
            filter.push(`{!tag=DETAIL_LEVEL}its_detail_weight:[0 TO ${detailLevelWeight}]`);
        }
        if (published !== null) {
            // See https://refstudycentre.atlassian.net/browse/VV-381 .
            // See https://refstudycentre.atlassian.net/browse/VV-382 .
            filter.push(`bs_status:${published ? "true" : "false"}`);
        }
        const result = this.select({
            query: "*:*",
            filter,
            offset,
            limit,
            fields: fields.join(","),
            params: {
                group: true,
                "group.field": "ss_computed_group",
                "group.offset": groupOffset,
                "group.limit": groupLimit,
            },
            facet: {
                languages: {
                    type: "terms",
                    field: "ss_search_api_language",
                    domain: {
                        // This causes the facets to give us counts over all languages, not just the ones in our query.
                        // See https://solr.apache.org/guide/8_10/json-faceting-domain-changes.html#filter-exclusions
                        excludeTags: ["COMMENTARY_TYPE", "LANGUAGE", "DETAIL_LEVEL"],
                    },
                    facet: {
                        commentaryTypes: {
                            type: "terms",
                            field: "sm_type_short_name",
                            domain: {
                                // This causes the facets to give us counts over all commentary types, not just the ones in our query.
                                // See https://solr.apache.org/guide/8_10/json-faceting-domain-changes.html#filter-exclusions
                                excludeTags: "COMMENTARY_TYPE",
                            },
                        },
                        detailLevels: {
                            type: "terms",
                            field: "its_detail_weight",
                            domain: {
                                // This causes the facets to give us counts over all detail levels, not just the ones in our query.
                                // See https://solr.apache.org/guide/8_10/json-faceting-domain-changes.html#filter-exclusions
                                excludeTags: "DETAIL_LEVEL",
                            },
                        },
                    },
                },
            },
        });
        return result.andThen((data) => {
            // Assume the data from the server is correct.
            return ok(data);
        });
    }
    /** @inheritDoc */
    getLatestAppNews(fields, published, languages, offset, limit) {
        const filter = ["index_id:app_news"];
        if (published !== null) {
            filter.push(`bs_status:${published ? "true" : "false"}`);
        }
        if (languages.length) {
            filter.push(`ss_search_api_language:(${languages.join(" OR ")})`);
        }
        const result = this.select({
            query: "*:*",
            filter,
            offset,
            limit,
            fields: fields.join(","),
            sort: "ds_created desc",
        });
        return result.andThen((data) => {
            const doclist = data.response;
            if (!doclist) {
                return err(new SolrBadDataError("Did not find `response` in response data."));
            }
            // Assume the data from the server is correct.
            return ok(doclist);
        });
    }
    /** @inheritDoc */
    queryCommentMoreLikeThis(nid, languages, mltFields, resultFields) {
        const query = [`its_nid:${nid}`];
        if (languages.length) {
            query.push(`ss_search_api_language:(${languages.join(" OR ")})`);
        }
        const result = this.select({
            query: query.join(" AND "),
            // N.B.: Using `filter` on this level does not affect the `mlt` results. Filtering for `bs_status:true` or
            // `index_id:commentary_comments` here would only filter the main results, which is not what we want to do.
            // Instead, we have to filter the `mlt` results after we get them back from Solr. This is unfortunately wasteful.
            fields: resultFields.join(","),
            params: {
                // Enable MoreLikeThis
                mlt: true,
                // Add all the fields to use to find similar comments
                "mlt.fl": mltFields,
                // Set up field boosts for priority.
                // Boosts use the following format `field^boost`.
                // Here we calculate the boosts based on the position of the field in the `fields` array.
                // The position of the field in the `fields` array is the decimal of one (1) in the boost,
                // so if the array has 3 entries, the first field would be "field^1.3" and the last would be "field^1.1"
                "mlt.qf": assignBoostInRange(1, 2, mltFields).map((field) => `${field.field}^${field.boost}`),
            },
        });
        return result.andThen((data) => {
            var _a, _b, _c;
            // Since we only request `moreLikeThis` for one comment at a time, just use the first value from the results.
            const docs = (_c = (_b = Object.values((_a = data.moreLikeThis) !== null && _a !== void 0 ? _a : {})[0]) === null || _b === void 0 ? void 0 : _b.docs) !== null && _c !== void 0 ? _c : [];
            // Assume the data from the server is correct.
            return ok(docs);
        });
    }
    /** @inheritDoc */
    queryTaxonomyTerms(tokens, vocabularies, published, language, offset, limit, resultFields) {
        const filter = [
            "index_id:taxonomy",
            `ss_vid:(${vocabularies.join(" OR ")})`,
            `ss_search_api_language:${language}`,
        ];
        if (published !== null) {
            filter.push(`bs_status:${published ? "true" : "false"}`);
        }
        const result = this.select({
            // `foo*^2 OR foo~` means: Fields starting with "foo" are twice as important as fields fuzzy matching "foo".
            // Using `AND` between tokens so that `['cla', 'bou']` matches "Clarence Bouwman" but not "Clarence Smith".
            query: tokens.length
                ? tokens.map((token) => `(${token}*^2 OR ${token}~)`).join(" AND ")
                : "*:*",
            filter,
            offset,
            limit,
            fields: resultFields.join(","),
            params: {
                defType: "edismax",
                qf: `tm_X3b_${language}_name tm_X3b_${language}_description^0.5`,
            },
        });
        return result.andThen((data) => {
            const doclist = data.response;
            if (!doclist) {
                return err(new SolrBadDataError("Did not find `response` in response data."));
            }
            // Assume the data from the server is correct.
            return ok(doclist);
        });
    }
    /** @inheritDoc */
    getTaxonomyTermsByUuid(uuids, fields, languages) {
        if (uuids.length < 1) {
            return okAsync({
                docs: [],
                numFound: 0,
                start: 0,
                numFoundExact: true,
            });
        }
        const filter = ["index_id:taxonomy", `ss_uuid:(${uuids.join(" OR ")})`];
        if (languages.length) {
            filter.push(`ss_search_api_language:(${languages.join(" OR ")})`);
        }
        const result = this.select({
            query: "*:*",
            filter,
            offset: 0,
            limit: 200,
            fields: fields.join(","),
        });
        return result.andThen((data) => {
            const doclist = data.response;
            if (!doclist) {
                return err(new SolrBadDataError("Did not find `response` in response data."));
            }
            // Assume the data from the server is correct.
            return ok(doclist);
        });
    }
    /** @inheritDoc */
    getNodeIds(index, published, languages, offset, limit) {
        const filter = [`index_id:${index}`];
        if (published !== null) {
            filter.push(`bs_status:${published ? "true" : "false"}`);
        }
        if (languages.length) {
            filter.push(`ss_search_api_language:(${languages.join(" OR ")})`);
        }
        const result = this.select({
            query: "*:*",
            filter,
            offset,
            limit,
            fields: "its_nid",
            // Sorting by ascending IDs ensures that new nodes are not added in between while paging.
            // The latest nodes will have the highest IDs, and therefore be part of the last page.
            sort: "its_nid asc",
        });
        return result.andThen((data) => {
            var _a;
            // Assume the data from the server is correct.
            return ok((((_a = data.response) === null || _a === void 0 ? void 0 : _a.docs) || []).map((doc) => doc["its_nid"]));
        });
    }
    /** @inheritDoc */
    getNodeIdsAndTranslations(index, published, languages, offset, limit) {
        const filter = [`index_id:${index}`];
        if (published !== null) {
            filter.push(`bs_status:${published ? "true" : "false"}`);
        }
        if (languages.length) {
            filter.push(`ss_search_api_language:(${languages.join(" OR ")})`);
        }
        const result = this.select({
            query: "*:*",
            filter,
            // The fields to retrieve for each translation of a node.
            fields: ["ds_changed", "ss_search_api_language"].join(","),
            // Sorting by ascending IDs ensures that new nodes are not added in between while paging.
            // The latest nodes will have the highest IDs, and therefore be part of the last page.
            sort: "its_nid asc",
            // The maximum number of groups in this page.
            limit,
            offset,
            params: {
                group: true,
                "group.field": "its_nid",
                // This limits the number of docs per group. We want all of them.
                "group.limit": -1,
            },
        });
        return result.andThen((data) => {
            // Assume the data from the server is correct.
            var _a, _b, _c;
            // Each group represents one node having one or more translations.
            const groups = (_c = (_b = (_a = data.grouped) === null || _a === void 0 ? void 0 : _a["its_nid"]) === null || _b === void 0 ? void 0 : _b.groups) !== null && _c !== void 0 ? _c : [];
            return ok(groups.map((group) => {
                var _a, _b;
                // Each doc in the group represents one translation of the node.
                const docs = (_b = (_a = group.doclist) === null || _a === void 0 ? void 0 : _a.docs) !== null && _b !== void 0 ? _b : [];
                return {
                    nid: group.groupValue,
                    translations: docs.map((doc) => ({
                        changed: new Date(doc.ds_changed),
                        lang: doc.ss_search_api_language,
                    })),
                };
            }));
        });
    }
    getNode(nid, language, fields, indexId) {
        const result = this.select({
            query: "*:*",
            filter: [
                `index_id:${indexId}`,
                `its_nid:${nid}`,
                `{!tag=LANGUAGE}ss_search_api_language:${language}`,
            ],
            offset: 0,
            limit: 1,
            fields: fields.join(","),
            facet: {
                languages: {
                    type: "terms",
                    field: "ss_search_api_language",
                    domain: {
                        // This causes the facets to give us counts over all languages, not just the ones in our query.
                        // See https://solr.apache.org/guide/8_10/json-faceting-domain-changes.html#filter-exclusions
                        excludeTags: "LANGUAGE",
                    },
                },
            },
        });
        return result.andThen((data) => {
            var _a, _b, _c, _d, _e;
            const facets = ((_a = data.facets) !== null && _a !== void 0 ? _a : {});
            return ok({
                doc: (_c = (_b = data.response) === null || _b === void 0 ? void 0 : _b.docs[0]) !== null && _c !== void 0 ? _c : null,
                languages: (_e = (_d = facets === null || facets === void 0 ? void 0 : facets.languages) === null || _d === void 0 ? void 0 : _d.buckets.map((b) => b.val)) !== null && _e !== void 0 ? _e : [],
            });
        });
    }
    getNodes(nids, fields, languages, indexId) {
        const filter = [`index_id:${indexId}`, `its_nid:(${nids.join(" OR ")})`];
        if (languages.length) {
            filter.push(`ss_search_api_language:(${languages.join(" OR ")})`);
        }
        const result = this.select({
            query: "*:*",
            filter,
            offset: 0,
            limit: 200,
            fields: fields.join(","),
        });
        return result.andThen((data) => {
            const doclist = data.response;
            if (!doclist) {
                return err(new SolrBadDataError("Did not find `response` in response data."));
            }
            // Assume the data from the server is correct.
            return ok(doclist);
        });
    }
}

export { SolrBadDataError, SolrClient, SolrClientError, SolrFetchError, SolrHttpError };
